import { useCallback } from 'react'

import {
  connectWallet,
  getConnectedAddress,
  signPersonalSignText,
} from '@/lib/lighter/sign-subaccount-key'
import {
  lighterConfirmIntegratorConsent,
  lighterPrepareIntegratorConsent,
} from '@/service/venue.api'
import type { LighterPrepareIntegratorConsentResponse } from '@/types/generated/public_api'

// Lighter Partner Attribution consent. Unlike Derive's builder fee (charged
// with no consent step at all), Lighter requires the customer to approve the
// integrator on-chain before any fee can be attributed — so this is a
// two-call exchange around one wallet signature, the same shape as the
// subaccount-key mint in use-lighter-onboard.
//
// The L1 half of ApproveIntegrator is plain `personal_sign`, the same
// signature family as ChangePubKey, so `signPersonalSignText` is reused
// verbatim rather than duplicated.
export function useLighterIntegratorConsent() {
  const prepare = useCallback(
    (accountId: string): Promise<LighterPrepareIntegratorConsentResponse> =>
      lighterPrepareIntegratorConsent({ accountId }),
    []
  )

  // Signs and confirms in one action. Throws on a rejected submission rather
  // than returning `ok: false` — the caller renders one error path, and a
  // silent false would look identical to a granted consent while the account
  // stayed unattributed.
  //
  // Reuses the wallet that is already connected and only falls back to the
  // picker when there is none. `connectWallet` disconnects the current session
  // before showing the picker, so calling it unconditionally would tear down a
  // perfectly good connection just to re-establish the same one.
  const signAndConfirm = useCallback(
    async (preparedConsentId: string, personalSignText: string): Promise<void> => {
      const walletAddress = (await getConnectedAddress()) ?? (await connectWallet())
      const personalSignSignature = await signPersonalSignText({
        walletAddress,
        personalSignText,
      })
      const result = await lighterConfirmIntegratorConsent({
        preparedConsentId,
        personalSignSignature,
      })
      if (!result?.ok) throw new Error(result?.error || 'Failed to record consent')
    },
    []
  )

  return { prepare, signAndConfirm }
}
